/**
 * BillingTab — F9 T9.5 (panel klienta, Settings).
 *
 * Self-service edycja danych do faktury platformy SaaS. Widoczna tylko dla
 * super-userow tenanta (gating w `Settings.tsx` — `me.user.isSuperUser`).
 *
 * 9 pol w 4 sekcjach: Dane firmy, NIP, Adres, Kraj.
 *   - 6 wymaganych: legalName, taxId, street, postalCode, city, countryCode.
 *   - 3 opcjonalne: buildingNo, apartmentNo, region.
 *
 * Walidacja klient-side: required (puste pole -> komunikat lokalny). Walidacja
 * NIP-u (mod 11 dla countryCode='PL') wykonywana po stronie serwera — backend
 * zwraca 400 VALIDATION_ERROR z `details.fieldErrors` ktore mapujemy na pola
 * (komunikat pod inputem `data-testid="billing-<field>-error"`).
 *
 * Save -> PUT `/auth/tenant-billing` -> toast success.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface BillingPayload {
  legalName: string | null;
  taxId: string | null;
  street: string | null;
  buildingNo: string | null;
  apartmentNo: string | null;
  postalCode: string | null;
  city: string | null;
  region: string | null;
  countryCode: string | null;
}

const TENANT_BILLING_KEY = ['tenant-billing'] as const;

const REQUIRED_FIELDS = [
  'legalName',
  'taxId',
  'street',
  'postalCode',
  'city',
  'countryCode',
] as const;

type FieldName = keyof BillingPayload;

interface FormState {
  legalName: string;
  taxId: string;
  street: string;
  buildingNo: string;
  apartmentNo: string;
  postalCode: string;
  city: string;
  region: string;
  countryCode: string;
}

function emptyForm(): FormState {
  return {
    legalName: '',
    taxId: '',
    street: '',
    buildingNo: '',
    apartmentNo: '',
    postalCode: '',
    city: '',
    region: '',
    countryCode: 'PL',
  };
}

function payloadToForm(p: BillingPayload): FormState {
  return {
    legalName: p.legalName ?? '',
    taxId: p.taxId ?? '',
    street: p.street ?? '',
    buildingNo: p.buildingNo ?? '',
    apartmentNo: p.apartmentNo ?? '',
    postalCode: p.postalCode ?? '',
    city: p.city ?? '',
    region: p.region ?? '',
    countryCode: p.countryCode ?? 'PL',
  };
}

function formToBody(f: FormState): Record<string, string | null> {
  return {
    legalName: f.legalName.trim(),
    taxId: f.taxId.trim(),
    street: f.street.trim(),
    buildingNo: f.buildingNo.trim() || null,
    apartmentNo: f.apartmentNo.trim() || null,
    postalCode: f.postalCode.trim(),
    city: f.city.trim(),
    region: f.region.trim() || null,
    countryCode: f.countryCode.trim().toUpperCase(),
  };
}

interface FieldRowProps {
  testId: string;
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  type?: string;
  maxLength?: number;
}

function FieldRow({
  testId,
  label,
  required,
  value,
  onChange,
  error,
  type,
  maxLength,
}: FieldRowProps) {
  const inputTestId = `billing-${testId}`;
  const errorTestId = `billing-${testId}-error`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputTestId} className="text-sm font-medium text-text-secondary">
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      <input
        id={inputTestId}
        data-testid={inputTestId}
        type={type ?? 'text'}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className="rounded-md border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none transition focus:border-accent-500"
      />
      {error && (
        <span data-testid={errorTestId} role="alert" className="text-xs text-red-600">
          {error}
        </span>
      )}
    </div>
  );
}

export function BillingTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const billingQ = useQuery({
    queryKey: TENANT_BILLING_KEY,
    queryFn: async () => {
      const res = await api.get('/auth/tenant-billing');
      return res.data as BillingPayload;
    },
  });

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});

  useEffect(() => {
    if (billingQ.data) setForm(payloadToForm(billingQ.data));
  }, [billingQ.data]);

  const updateField = (field: keyof FormState) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field as FieldName]) return prev;
      const copy = { ...prev };
      delete copy[field as FieldName];
      return copy;
    });
  };

  const requiredErrorLabel = useMemo(() => t('tenant.settings.billing.errors.required'), [t]);

  const saveM = useMutation({
    mutationFn: async (body: Record<string, string | null>) => {
      const res = await api.put('/auth/tenant-billing', body);
      return res.data as BillingPayload;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(TENANT_BILLING_KEY, data);
      setErrors({});
      toast.show({ variant: 'success', title: t('tenant.settings.billing.toast.saved') });
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 400) {
        const data = err.response.data as
          | { error?: string; details?: Record<string, string[] | undefined> }
          | undefined;
        if (data?.error === 'VALIDATION_ERROR' && data.details) {
          const mapped: Partial<Record<FieldName, string>> = {};
          for (const [k, v] of Object.entries(data.details)) {
            const first = Array.isArray(v) ? v[0] : undefined;
            if (first) mapped[k as FieldName] = first;
          }
          setErrors(mapped);
          return;
        }
      }
      toast.show({ variant: 'error', title: t('tenant.settings.billing.toast.error') });
    },
  });

  const onSave = () => {
    const localErrors: Partial<Record<FieldName, string>> = {};
    for (const f of REQUIRED_FIELDS) {
      const v = form[f as keyof FormState] ?? '';
      if (!v.trim()) localErrors[f] = requiredErrorLabel;
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    saveM.mutate(formToBody(form));
  };

  if (billingQ.isLoading) {
    return (
      <Card padding="md">
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (billingQ.isError) {
    return (
      <Card padding="md">
        <p className="text-sm text-red-600">{t('tenant.settings.billing.errors.load')}</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="billing-tab">
      <Card padding="md">
        <h2 className="mb-3 text-base font-semibold text-text-primary">
          {t('tenant.settings.billing.sections.company')}
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          <FieldRow
            testId="legal-name"
            label={t('tenant.settings.billing.fields.legalName')}
            required
            value={form.legalName}
            onChange={updateField('legalName')}
            error={errors.legalName}
            maxLength={255}
          />
          <FieldRow
            testId="tax-id"
            label={t('tenant.settings.billing.fields.taxId')}
            required
            value={form.taxId}
            onChange={updateField('taxId')}
            error={errors.taxId}
            maxLength={20}
          />
        </div>
      </Card>

      <Card padding="md">
        <h2 className="mb-3 text-base font-semibold text-text-primary">
          {t('tenant.settings.billing.sections.address')}
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          <FieldRow
            testId="street"
            label={t('tenant.settings.billing.fields.street')}
            required
            value={form.street}
            onChange={updateField('street')}
            error={errors.street}
            maxLength={200}
          />
          <div className="grid grid-cols-2 gap-3">
            <FieldRow
              testId="building-no"
              label={t('tenant.settings.billing.fields.buildingNo')}
              value={form.buildingNo}
              onChange={updateField('buildingNo')}
              error={errors.buildingNo}
              maxLength={20}
            />
            <FieldRow
              testId="apartment-no"
              label={t('tenant.settings.billing.fields.apartmentNo')}
              value={form.apartmentNo}
              onChange={updateField('apartmentNo')}
              error={errors.apartmentNo}
              maxLength={20}
            />
          </div>
          <FieldRow
            testId="postal-code"
            label={t('tenant.settings.billing.fields.postalCode')}
            required
            value={form.postalCode}
            onChange={updateField('postalCode')}
            error={errors.postalCode}
            maxLength={20}
          />
          <FieldRow
            testId="city"
            label={t('tenant.settings.billing.fields.city')}
            required
            value={form.city}
            onChange={updateField('city')}
            error={errors.city}
            maxLength={100}
          />
          <FieldRow
            testId="region"
            label={t('tenant.settings.billing.fields.region')}
            value={form.region}
            onChange={updateField('region')}
            error={errors.region}
            maxLength={100}
          />
        </div>
      </Card>

      <Card padding="md">
        <h2 className="mb-3 text-base font-semibold text-text-primary">
          {t('tenant.settings.billing.sections.country')}
        </h2>
        <FieldRow
          testId="country-code"
          label={t('tenant.settings.billing.fields.countryCode')}
          required
          value={form.countryCode}
          onChange={(v) => updateField('countryCode')(v.toUpperCase())}
          error={errors.countryCode}
          maxLength={2}
        />
      </Card>

      <div className="flex justify-end">
        <Button
          variant="primary"
          data-testid="billing-save"
          onClick={onSave}
          disabled={saveM.isPending}
        >
          {saveM.isPending
            ? t('tenant.settings.billing.saving')
            : t('tenant.settings.billing.save')}
        </Button>
      </div>
    </div>
  );
}
